import { PropsWithChildren, useEffect, useState } from 'react';

import { GearbeltLoader } from '@gearbelt/ui/icons';
import { useQuery } from '@tanstack/react-query';
import { HTTPError } from 'ky';
import { useAuth } from 'react-oidc-context';
import { useNavigate } from 'react-router-dom';

import { GbmwRedirectLoader } from '@/components/gbmw-redirect-loader';
import { getRouteWorkspaceId, resolveWorkspaceDestination } from '@/components/resolve-workspace-destination';
import { AuthDenialModal } from '@/components/workspace/auth-denial-modal';
import { useGbmwDeviceRedirect } from '@/hooks/use-gbmw-device-redirect';
import { useRecordWorkspaceVisit } from '@/hooks/use-record-workspace-visit';
import { getCurrentWebUserQueryOptions } from '@/queries/current-user';
import { useUserStore } from '@/stores/user.store';
import { useIsAWA } from '@/utils/awa';
import { isWorkOrderDeepLinkPath } from '@/utils/routes';

export function AppBootstrap({ children }: PropsWithChildren) {
  const { isAWA } = useIsAWA();
  const { user: faUser, isLoading: faIsLoading } = useAuth();
  const { user, org, setUser, setOrg } = useUserStore();
  const navigate = useNavigate();
  const [loaderImageLoaded, setLoaderImageLoaded] = useState(false);
  const [isWorkspaceResolved, setIsWorkspaceResolved] = useState(false);

  const enabled = !isAWA && !faIsLoading && !!faUser;
  const { data: fetchedUser, isSuccess, isError, error } = useQuery(getCurrentWebUserQueryOptions({ enabled }));

  // Once the fresh authenticated user resolves, bounce phone-class browsers to
  // GBMW so any notification link (work order, issue, schedule, document) lands
  // on the mobile interface. Gated on `isSuccess` so a persisted (possibly
  // stale) store can't trigger a redirect before auth settles. Invitation links
  // (/activate, /onboard) are deliberately not redirected pre-auth — the
  // invitee has no account until those pages complete and GBMW has no
  // onboarding surface; they're bounced here after first login.
  // WorkOrderRedirectPage resolves the WO email deep-link to the specific WO on GBMW — don't shadow it with the root redirect.
  const isWorkOrderDeepLink = isWorkOrderDeepLinkPath(window.location.pathname);
  const isRoutingToGbmw = useGbmwDeviceRedirect(enabled && isSuccess && !isWorkOrderDeepLink);

  // Only a 401 means the session is gone; the api-v2 hook is already redirecting to signout, so the loader stays up.
  // A 403 is a permission failure the hook surfaces as a modal; any other failure gets an explicit retry prompt.
  const isExpiredSession = error instanceof HTTPError && error.response.status === 401;

  useEffect(() => {
    if (isAWA || faIsLoading || !faUser || !fetchedUser) {
      return;
    }
    setUser(fetchedUser);
    void resolveWorkspaceDestination(fetchedUser, { setOrg, navigate }).finally(() => setIsWorkspaceResolved(true));
  }, [fetchedUser, faUser, faIsLoading, isAWA, setUser, setOrg, navigate]);

  // `isWorkspaceResolved`, not just `isSuccess`: `org` still holds the rehydrated
  // workspace until the effect above settles, so stamping earlier records a visit
  // to whichever workspace the user was in last session rather than this one.
  useRecordWorkspaceVisit({ workspaceId: org?.id, isReady: !isAWA && isSuccess && isWorkspaceResolved });

  // Rendered outside the gate below so a coded denial explains itself from any
  // state — including this component's own `current-web-user` failure, which
  // otherwise lands on the generic error screen.
  function gate() {
    if (!isAWA && !!faUser && isError && !isExpiredSession) {
      return (
        <div className="flex h-dvh flex-col items-center justify-center gap-2 text-center">
          <span className="text-sm">Something went wrong loading Gearbelt.</span>
          <button onClick={() => window.location.reload()} className="text-default-blue text-sm underline">
            Refresh
          </button>
        </div>
      );
    }

    const isGateBlocking = !isAWA && (faIsLoading || (!!faUser && !(isSuccess && !!user?.roles && isWorkspaceResolved)));

    // Mount `children` early only when the persisted `org` already matches the URL's workspace —
    // otherwise ProtectedRoute/WorkspaceRoute could act on stale `org` (even navigating away entirely)
    // before resolveWorkspaceDestination corrects it, all hidden behind the overlay.
    const persistedOrgMatchesUrl = !!org && org.workspaceId === getRouteWorkspaceId(window.location.pathname);

    // Mount children under an opaque overlay instead of unmounting, so the loader isn't remounted
    // (and its animation restarted) when WorkOrderGbmwRedirectRoute later renders its own copy.
    if (isGateBlocking && isWorkOrderDeepLink && persistedOrgMatchesUrl) {
      return (
        <>
          {children}
          <div className="fixed inset-0 z-50 bg-white">
            <GbmwRedirectLoader />
          </div>
        </>
      );
    }

    // Hold the loader until the store has a fully-hydrated user AND the active workspace has been reconciled with the
    // URL. `user` rehydrates from persisted localStorage, so `isSuccess && user.roles` can be true a render before the
    // bootstrap effect runs; releasing then lets a route render against the stale persisted `org` and redirect/fetch the
    // previous workspace instead of the one named in the URL.
    if (isGateBlocking) {
      return (
        <div className="flex h-dvh flex-col items-center justify-center">
          <GearbeltLoader size={50} onLoad={() => setLoaderImageLoaded(true)} onError={() => setLoaderImageLoaded(true)} />
          {loaderImageLoaded && <div className="text-secondary-text mt-2 text-sm">Loading Gearbelt...</div>}
        </div>
      );
    }

    if (isRoutingToGbmw) {
      return <GbmwRedirectLoader />;
    }

    return <>{children}</>;
  }

  return (
    <>
      <AuthDenialModal />
      {gate()}
    </>
  );
}
